
import { l3draggable, l3droppable } from '@/directives/l3dragDrop';
import { Component, Vue, Prop, PropSync, Watch } from 'vue-property-decorator';
// components
import SimpleMessageBox from './SimpleMessageBox.vue';
import TusFileUploadOverlay from './TusFileUploadOverlay.vue';
// interfaces
import { FileDetailsVm } from '@/interfaces/FileDetailsVm';
import { namespace } from 'vuex-class';
import { CustomizationVm } from '@/interfaces/CustomizationVm';

const customization = namespace("customization");

@Component({
  components: {
    TusFileUploadOverlay,
    SimpleMessageBox
  },
  directives: { l3draggable, l3droppable }
})
export default class BackgroundAudioWithDnD extends Vue {
  @PropSync("audio", { type: String, default: "" }) syncedAudio!: string | null;
  @Prop() relativePath!: string; // optional relative path to upload file to
  @Prop({ type: [Boolean], default: false }) emitDeleteEvent!: boolean;
  @Prop({ type: [Boolean], default: false }) fillContainer!: boolean;
  @Prop({ type: [Boolean], default: false }) uploadAudio!: boolean;
  @Prop({ type: [Number], default: null }) previewHeight!: number | null;
  @Prop({ type: String, default: 'center', validator: (v: string) => ['start','top','center','middle','end','bottom'].includes(v) })
  verticalAlign!: string;
  @Prop({ type: [Number], default: 300 }) maxWidth!: number;
  @Prop({ type: [Number], default: 60 }) maxHeight!: number;
  @Prop({ type: [Boolean], default: false }) disabled!: boolean;

  @customization.Getter customizationSettings!: CustomizationVm;

  fileToUpload: File | null = null;
  showConfirmDelete = false;
  audioLoadError: boolean = false;

  @Watch('syncedAudio', { immediate: true })
  onSyncedAudioChanged(val: string | null) {
    if (!val) {
      this.audioLoadError = false;
      return;
    }
  }

  get containerStyle() {
    const alignMap: { [key: string]: string } = {
      start: 'flex-start',
      top: 'flex-start',
      center: 'center',
      middle: 'center',
      end: 'flex-end',
      bottom: 'flex-end'
    };
    const align = alignMap[this.verticalAlign] || 'center';

    const style: any = {
      display: 'flex',
      alignItems: align,
      justifyContent: 'center',
      width: this.fillContainer ? '100%' : `${this.maxWidth}px`,
      minHeight: this.previewHeight ? `${this.previewHeight}px` : `${this.maxHeight}px`
    };

    if (this.maxWidth && !this.fillContainer) style.maxWidth = `${this.maxWidth}px`;

    return style;
  }

  onUploaded(filePath: string | null) {
    if (!filePath) return;
    this.syncedAudio = filePath;
    this.audioLoadError = false;
  }

  onCancelled() {
    this.fileToUpload = null;
  }

  openFileDialog() {
    const input = this.$refs.fileInput as HTMLInputElement | undefined;
    if (input) input.click();
  }

  onDownloadFile() {
    if (!this.syncedAudio) return;
    let filename = this.$globalHelper.extractFileName(this.syncedAudio);
    this.$globalHelper.download(this.syncedAudio, filename);
  }

  onFileSelected(e: Event) {
    const input = e.target as HTMLInputElement;
    if (!input || !input.files || input.files.length === 0) return;
    const file = input.files[0];

    if (!file.type.startsWith("audio/")) {
      this.$store.commit("ux/SB_FAILURE", {
        message: "Bei dieser Datei handelt es sich nicht um eine Audio-Datei.",
        timeout: 0
      });
      input.value = '';
      return;
    }

    if (this.uploadAudio) {
      this.fileToUpload = file;
    } else {
      const reader = new FileReader();
      reader.onload = (ev: any) => {
        this.syncedAudio = ev.target.result;
        input.value = '';
      };
      reader.readAsDataURL(file);
    }
  }

  onDeleteAudio() {
    if (this.emitDeleteEvent) {
      this.$emit('delete:audioWithDnD');
      return;
    }
    this.syncedAudio = null;
    this.audioLoadError = false;
  }

  onAudioError() {
    this.audioLoadError = true;
  }

  onAudioLoad() {
    this.audioLoadError = false;
  }

  get isDark(): boolean {
    try {
      return !!((this as any).$vuetify && (this as any).$vuetify.theme && (this as any).$vuetify.theme.dark);
    } catch (e) {
      return false;
    }
  }

  get overlayStyle() {
    return {
      background: this.isDark ? 'rgba(50,50,50,0.85)' : 'rgba(200,200,200,0.85)'
    };
  }

  get dropOptions() {
    return {
      accept: (data: any, ev?: DragEvent) => {
        // If we don't yet have payload (many browsers populate files only on drop),
        // allow the drag if the DataTransfer types indicate Files — the real validation
        // will happen on drop.
        try {
          const types = ev?.dataTransfer?.types ? Array.from(ev!.dataTransfer!.types as any) : ev?.dataTransfer?.types;
          if (types && types.includes && types.includes('Files')) return true;
        } catch (e) {}

        if (!data) return false;
        if (data?.fileType && (data.fileType == "Audio")) return true;
        if (Array.isArray(data) && data.length > 0) {
          const f = data[0];
          if (!f) return false;
          if (f.type && typeof f.type === 'string' && f.type.startsWith('audio/')) return true;
          if (f.name && typeof f.name === 'string' && /\.(mp3|wav|ogg|m4a|flac|aac|wma)$/i.test(f.name)) return true;
          return false;
        }
        return false;
      },
      hoverClass: "drop-hover",
      transferTypes: ["Files"],
      targetGroups: ["audio-file-details"],
      onDrop: async (data: any) => {
        if (data?.fileType && (data.fileType == "Audio")) {
          let fileDetails: FileDetailsVm = data;
          this.syncedAudio = fileDetails.filePath;
        } else if (Array.isArray(data) && data[0] && data[0].name && data[0].type) {
          if (!data[0].type.startsWith("audio/")) {
            this.$store.commit("ux/SB_FAILURE", {
              message: "Bei dieser Datei handelt es sich nicht um eine Audio-Datei.",
              timeout: 0
            });
            return;
          }
          if (this.uploadAudio) {
            this.fileToUpload = data[0];
          } else {
            const file = data[0];
            const reader = new FileReader();
            reader.onload = async (e: any) => {
              this.syncedAudio = e.target.result;
            };
            reader.readAsDataURL(file);
          }
        }
      }
    };
  }
}
